/**
 * BRANDS LIST PAGE
 * 
 * Browse all brands in the system
 * Includes search, filters, and sorting
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { AppIcons } from '../../constants/icons';
import { itemTypeMetaAlways } from '../../utils/itemTypeRoutes';
import { AppStyles } from '../../constants/categoryColors';
import { searchBrands, getBrands } from '../../services/brandService';
import type { Brand, BrandStatus } from '../../types/brand';
import type { ItemType } from '../../types';

/**
 * All 8 itemTypes as the filter-chip roster. Sorted for a stable UI —
 * polish first (most common), then the others alphabetized. Keep in
 * sync with the ItemType union in src/types/index.ts.
 */
const FILTER_TYPES: ItemType[] = [
  'polish',
  'acrylic',
  'cuticle_care',
  'embellishment',
  'plate',
  'powder',
  'tool',
  'transfer',
];

// Helper to convert Date or Timestamp to timestamp number
const toTimestamp = (date: Date | Timestamp | undefined): number => {
  if (!date) return 0;
  if (date instanceof Timestamp) return date.toMillis();
  if (date instanceof Date) return date.getTime();
  return 0;
};

// 'collectors' counts unique users who own at least one item from a brand
// (each user counted once). 'items' counts raw item copies across all
// collectors — a user with 3 bottles of Rose Gold counts as 3. Both stats
// are precomputed by recomputeBrandStats and stored on brand.stats.
type SortOption = 'name' | 'collectors' | 'items' | 'newest';

export default function BrandsListPage() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Brand[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<BrandStatus | 'all'>('all');
  const [showDefunct, setShowDefunct] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('collectors');
  const [sortDesc, setSortDesc] = useState(true);
  // Cross-brand type discovery — user selects one or more types they
  // want represented, we narrow the grid to brands whose `typesCovered`
  // includes ALL of them (AND semantics). Empty set = no type filter.
  const [selectedTypes, setSelectedTypes] = useState<Set<ItemType>>(new Set());

  const toggleType = (t: ItemType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  // Load initial brands
  useEffect(() => {
    async function loadBrands() {
      setLoading(true);
      setError(null);
      
      try {
        const result = await getBrands({
          limit: 100,  // Load more initially
          orderByField: 'name' // Use simple field to avoid index issues
        });
        setBrands(result.brands || []);
      } catch (err) {
        console.error('Error loading brands:', err);
        setError('Failed to load brands');
        setBrands([]); // Ensure brands is always an array
      } finally {
        setLoading(false);
      }
    }
    
    loadBrands();
  }, [sortBy]);

  // Handle search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }
    
    const debounceTimer = setTimeout(async () => {
      try {
        const results = await searchBrands(searchTerm, 20);
        setSearchResults(results || []);
      } catch (err) {
        console.error('Error searching brands:', err);
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Get filtered and sorted brands - with defensive checks
  const brandsToFilter = searchResults ?? brands ?? [];
  const displayBrands = brandsToFilter.filter(brand => {
    // Status filter
    if (statusFilter !== 'all' && brand.status !== statusFilter) return false;

    // Defunct filter
    if (!showDefunct && brand.isDefunct) return false;

    // Type-covered filter — AND semantics: brand must carry EVERY
    // selected type. Absent typesCovered = brand hasn't been through
    // recomputeBrandStats since Phase 8; we filter it out when any type
    // is selected (safer than showing a brand that MIGHT not carry the
    // requested type). Users can widen by clearing filters.
    if (selectedTypes.size > 0) {
      const covered = brand.stats?.typesCovered ?? [];
      for (const t of selectedTypes) {
        if (!covered.includes(t)) return false;
      }
    }

    return true;
  }).sort((a, b) => {
    const multiplier = sortDesc ? -1 : 1;
    
    switch (sortBy) {
      case 'collectors':
        return ((a.stats?.totalCollectors || 0) - (b.stats?.totalCollectors || 0)) * multiplier;
      case 'items':
        return ((a.stats?.totalItems || 0) - (b.stats?.totalItems || 0)) * multiplier;
      case 'newest':
        const aTime = toTimestamp(a.createdAt);
        const bTime = toTimestamp(b.createdAt);
        return (aTime - bTime) * multiplier;
      case 'name':
      default:
        return (a.name || '').localeCompare(b.name || '') * multiplier;
    }
  });

  // Status badge component
  const StatusBadge = ({ status }: { status: BrandStatus }) => {
    if (status === 'verified') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
          <AppIcons.BrandVerified className="w-3 h-3" />
          Verified
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
        <AppIcons.BrandCommunity className="w-3 h-3" />
        Community
      </span>
    );
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="glass-card p-6 mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <AppIcons.Brands className="w-8 h-8 text-glass" />
            <h1 className="text-3xl font-heading text-glass">Browse Brands</h1>
          </div>
          <p className="text-glass-secondary font-body">
            Discover nail polish brands and explore their collections
          </p>
        </div>

        {/* Search & Filters */}
        <div className="glass-card p-4 mb-6">
          {/* Search */}
          <div className="relative mb-4">
            <AppIcons.SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search brands..."
              className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Type-carried filter — cross-brand discovery. Click a chip
              to narrow to brands that carry that itemType. Multi-select
              is AND: brand must carry every selected chip.
              Data source: `brand.stats.typesCovered` populated by
              `recomputeBrandStats`. */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs uppercase tracking-wide text-glass-muted font-medium mr-1">
              Types
            </span>
            {FILTER_TYPES.map((t) => {
              const meta = itemTypeMetaAlways(t);
              const active = selectedTypes.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={
                    active
                      ? 'inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-purple-500 text-white'
                      : 'inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-slate-700/50 text-glass hover:bg-slate-600/50 border border-slate-600'
                  }
                  aria-pressed={active}
                >
                  <span aria-hidden="true">{meta.emoji}</span>
                  {meta.label}
                </button>
              );
            })}
            {selectedTypes.size > 0 && (
              <button
                type="button"
                onClick={() => setSelectedTypes(new Set())}
                className="text-xs text-glass-muted underline hover:text-glass ml-1"
              >
                Clear
              </button>
            )}
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status filter */}
            <div className="flex items-center gap-2">
              <AppIcons.FilterIcon className="w-4 h-4 text-glass-muted" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as BrandStatus | 'all')}
                className="text-sm bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-1.5 text-glass focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Brands</option>
                <option value="verified">Verified Only</option>
                <option value="community">Community Only</option>
              </select>
            </div>

            {/* Show defunct toggle */}
            <label className="flex items-center gap-2 text-sm text-glass-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={showDefunct}
                onChange={(e) => setShowDefunct(e.target.checked)}
                className="rounded bg-slate-700/50 border-slate-600 text-purple-600 focus:ring-purple-500"
              />
              Show closed brands
            </label>

            {/* Sort */}
            <div className="flex items-center gap-2 ml-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="text-sm bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-1.5 text-glass focus:ring-2 focus:ring-purple-500"
              >
                <option value="collectors">Most Collected</option>
                <option value="items">Most Owned</option>
                <option value="name">Alphabetical</option>
                <option value="newest">Newest</option>
              </select>
              <button
                onClick={() => setSortDesc(!sortDesc)}
                className="p-1.5 border border-slate-600 rounded-lg hover:bg-slate-600/50"
                title={sortDesc ? 'Descending' : 'Ascending'}
              >
                {sortDesc ? (
                  <AppIcons.SortDescending className="w-4 h-4 text-glass-muted" />
                ) : (
                  <AppIcons.SortAscending className="w-4 h-4 text-glass-muted" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50/90 backdrop-blur-sm border border-red-200 rounded-xl text-red-700">
            <AppIcons.Error className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Results count */}
        {!loading && !error && (
          <p className="text-sm text-black mb-4">
            {searchTerm && searchResults
              ? `Found ${displayBrands.length} brands matching "${searchTerm}"`
              : `Showing ${displayBrands.length} brands`
            }
          </p>
        )}

        {/* Brands Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayBrands.map(brand => (
              <Link
                key={brand.id}
                to={`/brands/${brand.slug}`}
                className={`${AppStyles.cardHover} overflow-hidden group`}
              >
                {/* Banner/Logo area */}
                <div className="h-24 bg-gray-200/80 backdrop-blur-sm relative">
                  {brand.banner && (
                    <img
                      src={brand.banner}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}

                  {/* Logo */}
                  <div className="absolute -bottom-6 left-4">
                    {brand.logo ? (
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="w-12 h-12 rounded-lg object-cover border-2 border-white shadow-sm"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-lg ${AppStyles.avatarFallback} flex items-center justify-center border-2 border-white shadow-sm`}>
                        <span className="text-xl font-bold text-white">
                          {brand.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 pt-8">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                        {brand.name}
                      </h3>
                      {brand.isDefunct && (
                        <span className="text-xs text-red-600 font-medium">
                          Closed {brand.closed && `(${brand.closed})`}
                        </span>
                      )}
                    </div>
                    <StatusBadge status={brand.status} />
                  </div>

                  {brand.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {brand.description}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1" title="Products">
                      <AppIcons.ViewAll className="w-4 h-4" />
                      {brand.stats?.totalItems || 0}
                    </span>
                    <span className="flex items-center gap-1" title="Collectors">
                      <AppIcons.Collectors className="w-4 h-4" />
                      {brand.stats?.totalCollectors || 0}
                    </span>
                    {brand.ratings?.overall?.average && (
                      <span className="flex items-center gap-1" title="Rating">
                        <AppIcons.Wishlist className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        {brand.ratings.overall.average.toFixed(1)}
                      </span>
                    )}
                  </div>

                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && displayBrands.length === 0 && (
          <div className="text-center py-12">
            <AppIcons.ViewAll className="w-12 h-12 text-black mx-auto mb-4" />
            <h3 className="text-lg font-medium text-black mb-2">No brands found</h3>
            <p className="text-black">
              {searchTerm
                ? `No brands match "${searchTerm}". Try a different search.`
                : 'No brands match your filters.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
